/**
 * Student join (docs/06-UI-SPEC.md §4).
 *
 * Three steps, no account, no email, no password — ever (SRS B3). The last step is a
 * single tap, which is what unlocks audio on the student's device (SRS B3.1).
 */

import { useEffect, useState } from 'react';

import { ROOM_CODE_LENGTH } from '@opal/shared';

import { checkRoomCode, joinLesson } from '../api.js';
import { Logo } from '../assets/Logo.js';
import { CodeStep, ConfirmStep, NameStep } from '../components/join/JoinSteps.js';
import { ProgressDots } from '../components/join/ProgressDots.js';
import { navigate, useQueryParam } from '../router.js';

const UNKNOWN_CODE = "That code doesn't match a class. Please check with your teacher.";
const STEPS = 3;

/** True on devices whose main pointer is a finger — they get the on-screen keypad. */
function usesKeypad(): boolean {
  return window.matchMedia('(pointer: coarse)').matches;
}

function BackArrow({ onBack }: { readonly onBack: () => void }) {
  return (
    <button
      type="button"
      aria-label="Go back"
      onClick={onBack}
      className="absolute left-4 top-4 flex h-touch w-touch items-center justify-center rounded-md text-xl text-text-secondary transition duration-fast ease-out hover:bg-surface-hover"
    >
      ←
    </button>
  );
}

interface JoinState {
  readonly step: number;
  readonly code: string;
  readonly name: string;
  readonly error: string | null;
  readonly busy: boolean;
  readonly keypad: boolean;
}

interface StepViewProps {
  readonly state: JoinState;
  readonly setCode: (code: string) => void;
  readonly setName: (name: string) => void;
  readonly onCodeContinue: () => void;
  readonly onNameContinue: () => void;
  readonly onJoin: () => void;
}

function StepView({
  state,
  setCode,
  setName,
  onCodeContinue,
  onNameContinue,
  onJoin,
}: StepViewProps) {
  if (state.step === 1) {
    return (
      <CodeStep
        code={state.code}
        onCodeChange={setCode}
        onContinue={onCodeContinue}
        error={state.error}
        checking={state.busy}
        useKeypad={state.keypad}
      />
    );
  }
  if (state.step === 2) {
    return <NameStep name={state.name} onNameChange={setName} onContinue={onNameContinue} />;
  }
  return (
    <ConfirmStep
      code={state.code}
      name={state.name.trim()}
      onJoin={onJoin}
      joining={state.busy}
      error={state.error}
    />
  );
}

const START: JoinState = { step: 1, code: '', name: '', error: null, busy: false, keypad: false };

/** Holds the three steps' answers and does the two server calls the flow needs. */
function useJoinFlow(codeFromLink: string | null) {
  const [state, setState] = useState<JoinState>(START);
  const patch = (change: Partial<JoinState>): void => {
    setState((current) => ({ ...current, ...change }));
  };

  useEffect(() => {
    // A teacher's join link carries the code, so the student only types their name.
    const code = (codeFromLink ?? '').replace(/\D/g, '').slice(0, ROOM_CODE_LENGTH);
    setState((current) => ({ ...current, keypad: usesKeypad(), code: code || current.code }));
  }, [codeFromLink]);

  return {
    state,
    patch,
    checkCode: (): void => {
      patch({ busy: true, error: null });
      void checkRoomCode(state.code).then((matches) => {
        patch(matches ? { busy: false, step: 2 } : { busy: false, error: UNKNOWN_CODE });
      });
    },
    join: (): void => {
      patch({ busy: true, error: null });
      void joinLesson(state.code, state.name).then((result) => {
        patch({ busy: false, error: result.ok ? null : result.message });
        if (result.ok) {
          navigate('/board', {
            code: state.code,
            name: result.value.displayName,
            board: result.value.boardId,
            // Their place in the class. The board presents it and the server checks
            // it before deciding what they may do (SRS D4).
            pid: result.value.participantId,
          });
        }
      });
    },
  };
}

export function JoinPage() {
  const { state, patch, checkCode, join } = useJoinFlow(useQueryParam('code'));

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-8 bg-surface-sunken p-6">
      <Logo size={40} />
      <div className="relative flex w-full max-w-[440px] flex-col items-center gap-6 rounded-xl bg-surface p-12 text-center shadow-lg max-[640px]:p-6">
        {state.step > 1 ? (
          <BackArrow
            onBack={() => {
              patch({ step: state.step - 1, error: null });
            }}
          />
        ) : null}
        <ProgressDots total={STEPS} current={state.step} />
        <StepView
          state={state}
          setCode={(code) => {
            patch({ code, error: null });
          }}
          setName={(name) => {
            patch({ name });
          }}
          onCodeContinue={checkCode}
          onNameContinue={() => {
            patch({ step: 3 });
          }}
          onJoin={join}
        />
      </div>
    </main>
  );
}
